import { CheckCircle, Circle, MinusCircle } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';

import type {
  ServiceAggregateState,
  ServiceState,
} from './useBulkSOWOperations';

interface ServiceTogglePanelProps {
  services: ServiceAggregateState[];
  selectedCount: number;
  pendingChanges: Map<string, 'enable' | 'disable'>;
  onToggleChange: (
    serviceName: string,
    action: 'enable' | 'disable' | null,
  ) => void;
  onApply: () => void;
  isLoading?: boolean;
}

const getStateIcon = (state: ServiceState) => {
  switch (state) {
    case 'all-on':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'all-off':
      return <Circle className="h-4 w-4 text-zinc-400" />;
    case 'mixed':
      return <MinusCircle className="h-4 w-4 text-amber-500" />;
    default:
      return <Circle className="h-4 w-4 text-zinc-300" />;
  }
};

const getStateLabel = (
  state: ServiceState,
  onCount: number,
  offCount: number,
) => {
  switch (state) {
    case 'all-on':
      return `All ${onCount} on`;
    case 'all-off':
      return `All ${offCount} off`;
    case 'mixed':
      return `${onCount} on, ${offCount} off`;
    default:
      return 'Select firms';
  }
};

const getStateBadgeColor = (
  state: ServiceState,
): 'green' | 'zinc' | 'amber' | 'blue' => {
  switch (state) {
    case 'all-on':
      return 'green';
    case 'all-off':
      return 'zinc';
    case 'mixed':
      return 'amber';
    default:
      return 'blue';
  }
};

export const ServiceTogglePanel: React.FC<ServiceTogglePanelProps> = ({
  services,
  selectedCount,
  pendingChanges,
  onToggleChange,
  onApply,
  isLoading = false,
}) => {
  const hasPendingChanges = pendingChanges.size > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-zinc-200 dark:border-zinc-800">
        <div>
          <h3 className="font-medium text-sm">Services</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {selectedCount > 0
              ? `Toggle services for ${selectedCount} selected firm${selectedCount !== 1 ? 's' : ''}`
              : 'Select firms to see service states'}
          </p>
        </div>
        <Button
          color="blue"
          onClick={onApply}
          disabled={!hasPendingChanges || isLoading || selectedCount === 0}
          className="text-sm"
        >
          {isLoading ? 'Applying...' : 'Apply Changes'}
        </Button>
      </div>

      {/* Pending changes summary */}
      {hasPendingChanges && (
        <div className="px-3 py-2 bg-blue-50 dark:bg-blue-950/30 border-b border-blue-200 dark:border-blue-800">
          <p className="text-xs font-medium text-blue-700 dark:text-blue-300">
            {pendingChanges.size} pending change
            {pendingChanges.size !== 1 ? 's' : ''}
          </p>
          <div className="flex flex-wrap gap-1 mt-1">
            {Array.from(pendingChanges.entries()).map(([name, action]) => (
              <Badge
                key={name}
                color={action === 'enable' ? 'green' : 'zinc'}
                className="text-[10px]"
              >
                {name}: {action}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Service list */}
      <div className="flex-1 overflow-auto p-3 space-y-2">
        {services.map((service) => {
          const pendingAction = pendingChanges.get(service.serviceName);
          const isPending = pendingAction !== undefined;

          return (
            <div
              key={service.serviceId}
              className={cn(
                'border rounded-lg p-3 transition-colors',
                isPending
                  ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20'
                  : 'border-zinc-200 dark:border-zinc-800',
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getStateIcon(service.state)}
                  <span className="font-medium text-sm">
                    {service.displayName}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {/* Current state badge */}
                  {selectedCount > 0 && (
                    <Badge
                      color={getStateBadgeColor(service.state)}
                      className="text-[10px]"
                    >
                      {getStateLabel(
                        service.state,
                        service.onCount,
                        service.offCount,
                      )}
                    </Badge>
                  )}

                  {/* Toggle buttons */}
                  <div className="flex items-center border rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() =>
                        onToggleChange(
                          service.serviceName,
                          pendingAction === 'enable' ? null : 'enable',
                        )
                      }
                      disabled={selectedCount === 0}
                      className={cn(
                        'px-3 py-1 text-xs font-medium transition-colors',
                        pendingAction === 'enable'
                          ? 'bg-green-500 text-white'
                          : service.state === 'all-on' && !isPending
                            ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                            : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700',
                        selectedCount === 0 && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      ON
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        onToggleChange(
                          service.serviceName,
                          pendingAction === 'disable' ? null : 'disable',
                        )
                      }
                      disabled={selectedCount === 0}
                      className={cn(
                        'px-3 py-1 text-xs font-medium transition-colors border-l border-zinc-300 dark:border-zinc-600',
                        pendingAction === 'disable'
                          ? 'bg-zinc-600 text-white'
                          : service.state === 'all-off' && !isPending
                            ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300'
                            : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700',
                        selectedCount === 0 && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      OFF
                    </button>
                  </div>
                </div>
              </div>

              {/* Pending change indicator */}
              {isPending && (
                <div className="mt-2 flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                  <span>
                    Will {pendingAction} for {selectedCount} firm
                    {selectedCount !== 1 ? 's' : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => onToggleChange(service.serviceName, null)}
                    className="ml-2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 underline"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {services.length === 0 && (
          <div className="flex items-center justify-center h-32 text-sm text-zinc-500">
            Loading services...
          </div>
        )}
      </div>
    </div>
  );
};
